import { RichDevice } from '@store';
import {
  RemoteServiceChartConfig,
  RemoteServiceDiagnosisMetadata,
  RemoteServiceFeatures,
  RemoteServiceRecommendations,
} from '@vi-iot/client';
import { AnnualReportTemplate } from '@vi-iot/client/apis/annual-report/annual-report.model';

export interface RemoteServiceState {
  startPointMs: number;
  endPointMs: number;
  chartsConfig: RemoteServiceChartConfig[];
  chartsFeatures: RemoteServiceFeatures;
  diagnosisData: RemoteServiceDiagnosisEntry[];
  allSummaryData: RemoteServiceRecommendations[];
  annualReportData: AnnualReportData;
  summaryStatus: LoadingStatus;
  diagnosisStatus: LoadingStatus;
  chartDataStatus: LoadingStatus;
  thresholdsStatus: ThresholdsLoadingStatus;
  error: {
    errorType: string;
    [key: string]: any;
  };
  featuresChartsExpanded: boolean;
  showSideNavEntries: boolean;
  ignoredRemoteServiceNotifications: string[];
  selectedDevice: RichDevice;
}

export interface FeaturesChartsDetails {
  chartsConfig: RemoteServiceChartConfig[];
  chartTimestamps: { pointStart: number; pointInterval: number };
  chartExtremes: ChartExtremes;
}

export interface ChartExtremes {
  startPointMs: number;
  endPointMs: number;
}

export interface LoadingStatus {
  loading: boolean;
  loaded: boolean;
  dataPresent: boolean;
  isGwWithIdDoesNotExist?: boolean;
}
export interface ThresholdsLoadingStatus {
  loading: boolean;
  loaded: boolean;
  savingThreshold: string;
}

export interface RemoteServiceDiagnosisEntry {
  boiler_id: string;
  boiler_material_number: number;
  diagnosis_date: string;
  diagnosis_id: string;
  diagnosis_id_display?: string;
  diagnosis_name: string;
  diagnosis_group: string;
  diagnosis_value: {
    [key: string]: string | number;
  };
  diagnosis_configurations: RemoteServiceThresholdConfiguration[];
  diagnosis_anomaly: string;
  diagnosis_priority: RemoteServicePriority;
  diagnosis_text: string;
  metrics: {
    [key: string]: string | number;
  };
  actions: string[];
  relevant_adf: string[];
  meta_information?: {
    parent_diagnosis?: string;
    release_stage?: string;
    audience?: RemoteServiceDiagnosisMetadata.Audience[];
    version: string;
    [key: string]: any;
  };
  selected: boolean;
  index: number;
  tags?: RemoteServiceDiagnosisTag[];
}

export enum RemoteServiceDiagnosisTag {
  Beta = 'beta',
  GoldError = 'goldError',
  InternalRsa = 'internalRsa',
}

export interface RemoteServiceThresholdConfiguration {
  group: string;
  name: string;
  unit: string;
  max: number;
  min: number;
  default: number;
  value: number;
  description: string;
  comment: string;
}

export interface DisplayedDiagnosisEntry {
  countOfAllEntries: number;
  displayedDiagnosisEntry: RemoteServiceDiagnosisEntry[];
}

export interface AnnualReportData {
  deviceSerial: string;
  gatewaySerial: string;
  processId: string;
  fileUri: string;
  reportTemplate: AnnualReportTemplate;
  loading: boolean;
  loaded: boolean;
  error: any;
}

export enum RemoteServicePriority {
  Information = 'information',
  Warning = 'warning',
  Critical = 'critical',
}

export enum RemoteServiceDiagnosisLoadingStatus {
  DataPresent,
  DataLoading,
  ServiceNotAvailable,
  DiagnosisCalculationsNotReadyYet,
  EmptySuccessData,
}

export enum RemoteServiceDiagnosisChartLoadingStatus {
  DataPresent,
  DataLoading,
  NoData,
}

export enum RemoteServiceFeaturesLoadingStatus {
  DataPresent,
  DataWithNotSupportedFeatures,
  DataLoading,
  NoData,
  ServiceNotAvailable,
}

export enum RemoteServiceResponseErrorType {
  DeviceNotFound = 'DEVICE_NOT_FOUND',
  DataNotFound = 'DATA_NOT_FOUND',
  InvalidTimerange = 'INVALID_TIMERANGE',
}

export enum AnnualReportErrors {
  ReportNotReady = 'REPORT_NOT_READY',
  ValidationError = 'VALIDATION_ERROR',
  UnsupportedMediaType = 'UNSUPPORTED_MEDIA_TYPE',
  NotAllowed = 'NOT_ALLOWED',
  NotFound = 'NOT_FOUND',
  PackageNotPaidFor = 'PACKAGE_NOT_PAID_FOR',
  DeviceNotFound = 'DEVICE_NOT_FOUND',
  CompanyNotFound = 'COMPANY_NOT_FOUND',
  BoilerNotFound = 'BOILER_NOT_FOUND',
  InstallationNotFound = 'INSTALLATION_NOT_FOUND',
  RequestTooLarge = 'REQUEST_TOO_LARGE',
  InternalServerError = 'INTERNAL_SERVER_ERROR',
  ExternalServerError = 'EXTERNAL_SERVICE_ERROR',
}

export const ANNUAL_REPORT_ERROR_TRANSLATIONS: { [key: string]: string } = {
  [AnnualReportErrors.ReportNotReady]: '+REMOTE_SERVICE.ANNUAL_REPORTS.ERRORS.REPORT_NOT_READY',
  [AnnualReportErrors.ValidationError]: '+REMOTE_SERVICE.ANNUAL_REPORTS.ERRORS.VALIDATION_ERROR',
  [AnnualReportErrors.NotFound]: '+REMOTE_SERVICE.ANNUAL_REPORTS.ERRORS.NOT_FOUND',
  [AnnualReportErrors.PackageNotPaidFor]:
    '+REMOTE_SERVICE.ANNUAL_REPORTS.ERRORS.PACKAGE_NOT_PAID_FOR',
  [AnnualReportErrors.DeviceNotFound]: '+REMOTE_SERVICE.ANNUAL_REPORTS.ERRORS.DEVICE_NOT_FOUND',
  [AnnualReportErrors.CompanyNotFound]: '+REMOTE_SERVICE.ANNUAL_REPORTS.ERRORS.COMPANY_NOT_FOUND',
  [AnnualReportErrors.BoilerNotFound]: '+REMOTE_SERVICE.ANNUAL_REPORTS.ERRORS.BOILER_NOT_FOUND',
  [AnnualReportErrors.InstallationNotFound]:
    '+REMOTE_SERVICE.ANNUAL_REPORTS.ERRORS.INSTALLATION_NOT_FOUND',
  [AnnualReportErrors.RequestTooLarge]: '+REMOTE_SERVICE.ANNUAL_REPORTS.ERRORS.REQUEST_TOO_LARGE',
  [AnnualReportErrors.InternalServerError]:
    '+REMOTE_SERVICE.ANNUAL_REPORTS.ERRORS.INTERNAL_SERVER_ERROR',
  [AnnualReportErrors.ExternalServerError]:
    '+REMOTE_SERVICE.ANNUAL_REPORTS.ERRORS.EXTERNAL_SERVICE_ERROR',
  [AnnualReportErrors.NotAllowed]: '+REMOTE_SERVICE.ANNUAL_REPORTS.ERRORS.NOT_ALLOWED',
  [AnnualReportErrors.UnsupportedMediaType]:
    '+REMOTE_SERVICE.ANNUAL_REPORTS.ERRORS.UNSUPPORTED_MEDIA_TYPE',
};

export enum AnomalyLevel {
  RootCause = '-1',
  Information = '0',
  Anomaly = '1',
}

export const remoteServiceIgnoreNotificationKey = 'remoteServiceIgnoreNotification';
export const remoteServiceNotReadyError = 'gateway with given id does not exist';

export const ALLOWED_DIAGNOSIS_IDS = ['fdd_71', 'fdd_77'];
